import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { setOntarioLocationCheckStatus } from '@/store/actions/commonActions';
import { OntarioLocationCheckStatus } from '@/models/enum/ontario-location-check-status.enum';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { envIsCanada } from '@/constants/constants';
import {
  selectDisableLocationSmart,
  selectTagsLoaded,
  selectUserId,
} from '@/store/selectors/userSelectors';
import { selectOntarioLocationCheckStatus } from '@/store/selectors/commonSelectors';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { useIntl } from 'react-intl';

export interface GeolocsPingEvent {
  gatewayStatus: 'OK' | 'BLOCKED' | 'BLACKLISTED';
  geoReason?: 'DENIED';
  type: 'OK' | 'OUT_OF_REGION' | 'NO_LOCATION' | 'DTB_CALC_ERROR' | 'PING_FAILED';
  failureCode?: string;
}

const SCRIPT_ID = 'mgeo-engine';
const SCRIPT_SRC = '/mgeo/mGeoJS-4.3.12.min.js';
const GEO_PING_SERVICE = 'ontario';

const useGeoLocationGeolocs = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const disableLocationSmart = useSelector(selectDisableLocationSmart);
  const ontarioGeoLocationStatus = useSelector(selectOntarioLocationCheckStatus);
  const tagsLoaded = useSelector(selectTagsLoaded);
  const userId = useSelector(selectUserId);
  const { openModal, closeModal } = useModal();

  const [ready, setReady] = useState(false);
  const pollIdRef = useRef<number | null>(null);
  const startedRef = useRef(false);
  const pingTokenRef = useRef<string | null>(null);
  const permissionTokenRef = useRef<string | null>(null);

  const intl = useIntl();

  const onPingResponse = (data: GeolocsPingEvent) => {
    if (data.gatewayStatus !== 'OK') {
      if (data.geoReason === 'DENIED') {
        triggerGTag(GTagEvents.geolocs_fail_denied);
        dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.BrowserPermission));
      } else {
        triggerGTag(GTagEvents.geolocs_fail, {
          error: `${data.failureCode} ${data.gatewayStatus} ${data.geoReason} ${data.type}`,
        });
        dispatch(
          setOntarioLocationCheckStatus(
            OntarioLocationCheckStatus.Error,
            data.failureCode?.startsWith('10')
              ? intl.formatMessage({ id: 'geo-location.error.10-description' })
              : undefined,
          ),
        );
      }
      openModal(MODALS.GEOLOCATION);
    } else {
      triggerGTag(GTagEvents.geolocs_success);
      dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.Done));
      closeModal(MODALS.GEOLOCATION);
    }
  };

  // Ensure script is present and mark ready when window.mGeoJS exists
  useEffect(() => {
    // Load script only for ontario
    if (!envIsCanada()) return;

    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = SCRIPT_SRC;
      s.defer = true;
      document.body.appendChild(s);
    }

    const check = () => {
      if (window.mGeoJS) {
        setReady(true);
        if (pollIdRef.current) {
          window.clearInterval(pollIdRef.current);
          pollIdRef.current = null;
        }
      }
    };

    check(); // check if script is already loaded before starting interval check
    if (!window.mGeoJS) {
      pollIdRef.current = window.setInterval(check, 100);
    }

    return () => {
      if (pollIdRef.current) {
        window.clearInterval(pollIdRef.current);
        pollIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (ontarioGeoLocationStatus === OntarioLocationCheckStatus.Retry && ready) {
      dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.InProgress));
      window.mGeoJS.ping({ geoPingService: GEO_PING_SERVICE }).then(onPingResponse);
    }
  }, [ontarioGeoLocationStatus]);

  // Start/stop the geolocation engine based on conditions
  useEffect(() => {
    // If script not loaded return
    if (!ready) return;

    const mGeoJS = window.mGeoJS;

    const cleanup = () => {
      if (mGeoJS) {
        try {
          mGeoJS.stop();
          if (pingTokenRef.current) mGeoJS.unsubscribe(pingTokenRef.current);
          if (permissionTokenRef.current) mGeoJS.unsubscribe(permissionTokenRef.current);
        } catch (err) {
          triggerGTag(GTagEvents.geolocs_fail_error, {
            error: `${err}`,
          });
          console.error(err);
        }
      }
      permissionTokenRef.current = null;
      pingTokenRef.current = null;
      startedRef.current = false;
    };

    // Conditions: script loaded, ontario, authenticated, no skip tag
    const shouldRun =
      ready &&
      mGeoJS &&
      tagsLoaded &&
      envIsCanada() &&
      isAuthenticated &&
      !disableLocationSmart &&
      userId != null;

    // If shouldn’t run, stop and clean up
    if (!shouldRun) {
      cleanup();

      if (tagsLoaded && envIsCanada()) {
        // If user logged out or got disableLocationSmart tag close modal/finish
        if (!isAuthenticated || disableLocationSmart) {
          dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.Done));
          closeModal(MODALS.GEOLOCATION);
        }
      }
      return;
    }

    if (startedRef.current) return; //If already running return

    try {
      pingTokenRef.current = mGeoJS.subscribe('onPingResponse', onPingResponse);

      dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.InProgress));
      mGeoJS.setIdentitiesInVault({
        playerIdentifier: userId,
      });
      mGeoJS
        .start({ ping: true, geoPingService: GEO_PING_SERVICE })
        .then(() => {
          // Subscribe on permission change after ping has been started to avoid double call
          permissionTokenRef.current = mGeoJS.subscribe(
            'permissionChanged',
            (event: 'GRANTED' | 'DENIED' | 'PROMPT') => {
              if (event === 'GRANTED') {
                dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.InProgress));
                mGeoJS.ping({ geoPingService: GEO_PING_SERVICE }).then(onPingResponse);
              } else {
                dispatch(
                  setOntarioLocationCheckStatus(OntarioLocationCheckStatus.BrowserPermission),
                );
                openModal(MODALS.GEOLOCATION);
              }
            },
          );
        })
        .catch((err: any) => {
          triggerGTag(GTagEvents.geolocs_fail_error, {
            error: `${err}`,
          });
          dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.Error));
          openModal(MODALS.GEOLOCATION);
        });
      startedRef.current = true;
    } catch (err: any) {
      triggerGTag(GTagEvents.geolocs_fail_error, {
        error: `${err}`,
      });
      dispatch(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.Error));
      openModal(MODALS.GEOLOCATION);
    }

    return () => {
      cleanup();
    };
  }, [ready, isAuthenticated, disableLocationSmart, tagsLoaded, userId]);

  return { ready: ready && !!window.mGeoJS };
};

export default useGeoLocationGeolocs;
